/**
 * The artist marketplace — designers, their artwork, and what they are owed.
 *
 * A designer is structurally the same actor as a vendor: an outside party who
 * sees a redacted slice of orders and is paid periodically. So the shapes here
 * deliberately mirror `Vendor`, `VendorJob` and `VendorSettlement` rather than
 * inventing a parallel vocabulary — including the reason `DesignerEarning`
 * exists at all, which is that Firestore has no field-level read control and an
 * artist must never be able to read `orders`.
 */
import { extractTax } from './pricing.js';
import { roundMinor } from './money.js';
/**
 * The DEFAULT share of the ex-GST goods value an artist earns.
 *
 * No longer "the" rate: an artist may set their own, per piece and per product
 * — see `resolveRoyaltyRatePct`. This is what applies to every artwork nobody
 * has touched, which today is all of them.
 */
export const ROYALTY_RATE_PCT = 20;
/**
 * An upper bound on an artist's rate, and NOT a policy.
 *
 * There is deliberately no cap on what an artist may charge: the royalty is an
 * uplift on top of our price, so a high rate costs us nothing and simply will
 * not sell. But `z.number()` happily accepts 1e308, and a rate that large
 * overflows the amount we hand Razorpay — which is the maths breaking, not a
 * business rule. This is set far beyond any rate a person would mean to type.
 *
 * Same reasoning the commission hourly rate already uses (commission.ts):
 * bounds as a typo guard, because anything saved here is live on a public page
 * seconds later with nobody in between.
 */
export const MAX_ROYALTY_RATE_PCT = 10_000;
/**
 * A rate the arithmetic can actually carry.
 *
 * Each rejection prevents a specific corruption rather than expressing an
 * opinion: a NEGATIVE rate inverts the money flow and has the artist paying us;
 * a NON-FINITE one turns every price on the page into NaN without an error; and
 * a FRACTIONAL one defeats the whole-paise arithmetic that splitRoyaltyWeighted
 * and the settlement totals depend on.
 */
export function isUsableRate(value) {
    return typeof value === 'number'
        && Number.isInteger(value)
        && value >= 0
        && value <= MAX_ROYALTY_RATE_PCT;
}
/**
 * The rate for one artwork on one product.
 *
 * Per-product override, then the piece's own default, then the global default.
 *
 * THERE IS NO MIGRATION AND THERE SHOULD NOT BE ONE. An artwork with no
 * `royaltyRates` resolves to ROYALTY_RATE_PCT, which is byte-identical to what
 * it charged before artists could set anything — so every existing piece is
 * correct by absence. Backfilling `20` into every document would buy no
 * behaviour change and would permanently foreclose ever moving the default.
 *
 * A stored value that fails `isUsableRate` falls THROUGH to the next tier
 * rather than throwing: the callable is the only writer and validates on the
 * way in, so this is unreachable — and if it ever is reached, a bad rate should
 * not be able to make a product unbuyable.
 */
export function resolveRoyaltyRatePct(artwork, productSlug) {
    const rates = artwork?.royaltyRates;
    const override = productSlug ? rates?.byProductSlug?.[productSlug] : undefined;
    if (isUsableRate(override))
        return override;
    if (isUsableRate(rates?.default))
        return rates.default;
    return ROYALTY_RATE_PCT;
}
/**
 * Root categories where somebody else's artwork makes sense to buy.
 *
 * Nobody buys an artist's design on a letterhead or a visiting card — those
 * carry the customer's OWN identity, and a 20% uplift on them is meaningless.
 * Merch is the opposite: the design is the entire reason for the purchase.
 *
 * Matched against a product's full `categoryIds` ancestor chain, so a leaf
 * category inherits its group's eligibility without being listed here.
 */
export const MARKETPLACE_ROOT_CATEGORIES = [
    'clothing-caps-bags',
    'mugs-albums-gifts',
    'drinkware',
    'labels-stickers-packaging',
    'signs-posters-marketing',
    // Wall art is the most natural home an artist's work has here: somebody
    // buying a canvas for a wall is choosing a picture, not supplying one.
    'photo-gifts-wall-art',
];
/**
 * What to call each of those when a human has to read it.
 *
 * Here rather than in each app because the artist choosing "Stickers & labels"
 * in the studio and the admin reviewing it must be looking at the same words —
 * `labels-stickers-packaging` is a slug, not a label, and two hand-maintained
 * copies of this map drift the moment a category is renamed.
 *
 * Typed against the constant, so adding a root without labelling it is a
 * compile error rather than a slug leaking onto somebody's screen.
 */
export const MARKETPLACE_CATEGORY_LABELS = {
    'clothing-caps-bags': 'T-shirts, caps & bags',
    'mugs-albums-gifts': 'Mugs & gifts',
    drinkware: 'Bottles & drinkware',
    'labels-stickers-packaging': 'Stickers & labels',
    'signs-posters-marketing': 'Posters & prints',
    'photo-gifts-wall-art': 'Canvas & wall art',
};
/** The label, or the raw id if something outside the marketplace slips in. */
export function marketplaceCategoryLabel(id) {
    return MARKETPLACE_CATEGORY_LABELS[id] ?? id;
}
/**
 * Whether an artist's work can be sold on a product in this ROOT category.
 *
 * Takes a resolved root rather than a product's `categoryIds`, because that
 * array is not reliably the full ancestor chain — `a3-posters` carries only
 * `['posters']` while `acrylic-signs` carries `['signage',
 * 'signs-posters-marketing']`. Matching against the raw array therefore misses
 * every product whose chain was never filled in, and posters are exactly the
 * kind of thing this marketplace is for. Callers resolve the root by walking
 * the category tree, the way the price list already does.
 */
export function isMarketplaceRoot(rootCategoryId) {
    if (!rootCategoryId)
        return false;
    return MARKETPLACE_ROOT_CATEGORIES.includes(rootCategoryId);
}
export const ART_PRODUCTS = [
    { productSlug: 'a3-posters', label: 'Poster', mockupType: 'poster' },
    { productSlug: 'canvas-prints', label: 'Canvas print', mockupType: 'canvas' },
    { productSlug: 'bulk-cotton-t-shirts', label: 'T-shirt', mockupType: 'tee' },
    { productSlug: 'ceramic-mugs', label: 'Mug', mockupType: 'mug' },
    { productSlug: 'tote-bags', label: 'Tote bag', mockupType: 'tote' },
];
/**
 * How many products one artwork may be shown on.
 *
 * Every selection is an image composited and stored when the piece is
 * published, so an artist ticking all five costs five renders and five files
 * for a page nobody scrolls to the bottom of. Three is enough to show range.
 */
export const MAX_SHOWCASE_PRODUCTS = 3;
/**
 * Which piece fronts each "Looks good on" tile.
 *
 * `products` is the tile order; `artworks` newest first, as published lists
 * come. The featured piece goes first in the running order, and every tile
 * then takes the piece with the FEWEST tiles so far among those that have
 * that mockup, so a body of work spreads across the section instead of the
 * newest upload filling it. A piece repeats only when nothing else has the
 * mockup — which is still true, and better than an empty tile.
 */
export function pickShowcaseArtworks(products, artworks, featuredArtworkId) {
    const featured = artworks.find((a) => a.id === featuredArtworkId);
    const order = featured ? [featured, ...artworks.filter((a) => a !== featured)] : [...artworks];
    const used = new Map();
    const tiles = [];
    for (const productSlug of products) {
        let best;
        for (const a of order) {
            if (!a.mockups?.[productSlug])
                continue;
            if (!best || (used.get(a.id) ?? 0) < (used.get(best.id) ?? 0))
                best = a;
        }
        if (!best)
            continue;
        used.set(best.id, (used.get(best.id) ?? 0) + 1);
        tiles.push({ productSlug, art: best });
    }
    return tiles;
}
export function artProductBySlug(slug) {
    return ART_PRODUCTS.find((p) => p.productSlug === slug);
}
/* ------------------------------------------------------------------ *
 * Money
 * ------------------------------------------------------------------ */
/**
 * What a line costs once an artist's work is on it.
 *
 * The royalty is an uplift ON TOP of our price, not a share of it — our margin
 * is untouched and the customer pays for the design. Because our prices already
 * include GST and the uplift is proportional, the displayed price is simply
 * base × (1 + rate), which is why this looks too simple to need a function.
 * It needs one because the ROYALTY is not that number.
 */
/**
 * Divides a pack line's royalty between the designs on it, one equal share
 * per slot, so an artist with two of six stickers earns a third.
 *
 * Shares are whole paise and sum EXACTLY to the total: the remainder after
 * flooring goes one paisa at a time to the earliest slots. Rounding each share
 * independently would lose or invent paise, and the settlement adds these up.
 * Per-slot rather than per-artist so the split does not depend on how the
 * slots happen to be grouped; the same design twice is merged into one share.
 */
export function splitRoyalty(totalMinor, slots) {
    // Every slot equal: the original behaviour, now expressed through the
    // weighted split so there is one remainder algorithm in this file, not two.
    return splitRoyaltyWeighted(totalMinor, slots.map((s) => ({ ...s, ratePct: 1 })));
}
/**
 * The same split, weighted by what each artist charges.
 *
 * An artist at 60% on one of three slots should take more of the pool than one
 * at 10%, and the shares must still sum EXACTLY to the pool — settlement adds
 * these up, and a paisa invented here is a paisa paid out that nobody collected.
 *
 * THE TIE-BREAK IS LOAD-BEARING. Remainders go to the largest fractional parts,
 * and ties break by EARLIEST slot. At uniform weights every fraction is
 * identical, so that rule is exactly what makes this reduce to the equal split
 * it replaced — pack.test.ts passes unchanged, which is the proof. A sort that
 * is not index-stable would change historical behaviour invisibly.
 *
 * Per slot rather than per artist, so the split does not depend on how the
 * slots happen to be grouped; the same design twice is merged afterwards.
 */
export function splitRoyaltyWeighted(totalMinor, slots) {
    if (slots.length === 0)
        return [];
    const weights = slots.map((s) => (isUsableRate(s.ratePct) ? s.ratePct : 0));
    const weightSum = weights.reduce((a, b) => a + b, 0);
    // Everybody set 0%: no pool to split, and no division by zero.
    if (weightSum <= 0) {
        const byArtwork = new Map();
        for (const slot of slots) {
            if (!byArtwork.has(slot.artworkId))
                byArtwork.set(slot.artworkId, { ...slot, royaltyMinor: 0 });
        }
        return [...byArtwork.values()];
    }
    const exact = weights.map((w) => (totalMinor * w) / weightSum);
    const shares = exact.map((v) => Math.floor(v));
    let remainder = totalMinor - shares.reduce((a, b) => a + b, 0);
    const order = exact
        .map((v, i) => ({ i, frac: v - Math.floor(v) }))
        .sort((a, b) => (b.frac - a.frac) || (a.i - b.i));
    for (const { i } of order) {
        if (remainder <= 0)
            break;
        shares[i] = (shares[i] ?? 0) + 1;
        remainder -= 1;
    }
    const byArtwork = new Map();
    slots.forEach((slot, i) => {
        const existing = byArtwork.get(slot.artworkId);
        if (existing)
            existing.royaltyMinor += shares[i] ?? 0;
        else
            byArtwork.set(slot.artworkId, { ...slot, royaltyMinor: shares[i] ?? 0 });
    });
    return [...byArtwork.values()];
}
/**
 * Who earned what on one order line.
 *
 * A pack line carries `royaltySplit`, one entry per design on the sheet; a
 * single-design line carries the artist on the line itself. Both come out the
 * same shape, so settlement and the studio need not know which paid them.
 *
 * Lives here rather than in functions/ so it can be tested at all — it is pure,
 * and the module it came from pulls in firebase-admin.
 *
 * TWO THINGS IT MUST KEEP DOING:
 *
 * Prefer the SHARE's rate over the line's. A mixed-rate pack has a blended
 * `royaltyRatePct` that nobody chose, and writing it onto six artists' earnings
 * rows tells the one charging 60% that she earned 21.67%.
 *
 * Fall back to the line's rate when a share has none. Every pack line written
 * before artists could set rates has shares without `ratePct`, and those orders
 * are still settling.
 *
 * Zero-royalty shares are dropped. A 0% rate is legal now, and an earnings row
 * for ₹0 would reach a statement and a "you sold something" email.
 */
export function royaltySharesOfLine(item) {
    if (item.royaltySplit?.length) {
        return item.royaltySplit
            .map((r, i) => ({
            suffix: `_${i}`,
            designerId: r.designerId,
            artworkId: r.artworkId,
            artworkTitle: r.artworkTitle,
            royaltyMinor: r.royaltyMinor,
            ratePct: r.ratePct ?? item.royaltyRatePct ?? 0,
        }))
            .filter((r) => r.royaltyMinor > 0);
    }
    if (item.artworkId && item.designerId && item.royaltyMinor) {
        return [{
                suffix: '',
                designerId: item.designerId,
                artworkId: item.artworkId,
                artworkTitle: item.productName,
                royaltyMinor: item.royaltyMinor,
                ratePct: item.royaltyRatePct ?? 0,
            }];
    }
    return [];
}
export function artistPriceMinor(baseMinor, ratePct = ROYALTY_RATE_PCT) {
    return roundMinor(baseMinor * (1 + ratePct / 100));
}
/**
 * What the artist earns on that line.
 *
 * Computed on the ex-GST goods value, so an artist is paid on goods and never
 * on tax we collect and remit. That is why this is not simply
 * `artistPrice - base`: on a ₹290 mug at 18% those differ by ₹8.85, and the
 * larger figure would have us paying royalty on the government's money.
 */
export function royaltyMinor(baseMinor, gstRatePercent, ratePct = ROYALTY_RATE_PCT) {
    const { taxable } = extractTax(baseMinor, gstRatePercent);
    return roundMinor((taxable * ratePct) / 100);
}
/**
 * Both figures together, for a line of `qty` at a GST-inclusive unit price.
 *
 * `ratePct` is passed in rather than read from the constant so that a rate
 * change never rewrites what a past order paid — the caller snapshots the rate
 * onto the order line and replays it from there.
 */
export function royaltyFor(input) {
    const ratePct = input.ratePct ?? ROYALTY_RATE_PCT;
    return {
        chargedMinor: artistPriceMinor(input.baseSubtotalMinor, ratePct),
        royaltyMinor: royaltyMinor(input.baseSubtotalMinor, input.gstRatePercent, ratePct),
        ratePct,
    };
}
/**
 * A pack line, where each slot may carry a different artist's rate.
 *
 * WHY THE MEAN, AND WHY IT IS NOT AN APPROXIMATION. Every slot owns an equal
 * 1/N of the line — that is already what splitRoyalty assumes. So the uplift is
 *
 *     Σ (base/N)(rᵢ/100)  =  base × mean(r) / 100
 *
 * an identity, before any rounding. Charging the line once at the mean is
 * therefore exactly the sum of the per-slot uplifts, and it rounds ONCE.
 *
 * Summing independently-rounded per-slot uplifts instead would drift: base 1000
 * over three slots at a uniform 20% gives 334/333/333 → 67+67+67 = 201 paise
 * where the line rounds to 200. That is one paisa on every uniform-rate pack,
 * forever, for nothing — and it would silently reprice every pack already sold.
 *
 * At uniform rates mean = ROYALTY_RATE_PCT, so this produces byte-identical
 * figures to what it replaced. That is what keeps existing packs unchanged.
 *
 * The pool is then split WEIGHTED by rate, so the artist charging more takes
 * more of it, while the shares still sum exactly to the pool.
 */
export function packRoyalty(input) {
    const { slots } = input;
    if (slots.length === 0) {
        return {
            chargedMinor: input.baseSubtotalMinor,
            royaltyMinor: 0,
            blendedRatePct: 0,
            shares: [],
        };
    }
    const rates = slots.map((s) => (isUsableRate(s.ratePct) ? s.ratePct : ROYALTY_RATE_PCT));
    const blendedRatePct = rates.reduce((a, b) => a + b, 0) / rates.length;
    const line = royaltyFor({
        baseSubtotalMinor: input.baseSubtotalMinor,
        gstRatePercent: input.gstRatePercent,
        ratePct: blendedRatePct,
    });
    return {
        chargedMinor: line.chargedMinor,
        royaltyMinor: line.royaltyMinor,
        // Two decimals: a blended rate is rarely whole, and this is only ever shown
        // or stored as a label. Every figure that matters is a minor amount.
        blendedRatePct: Math.round(blendedRatePct * 100) / 100,
        shares: splitRoyaltyWeighted(line.royaltyMinor, slots.map((s, i) => ({ ...s, ratePct: rates[i] }))),
    };
}
/**
 * Days after delivery before a royalty can be paid out.
 *
 * Earnings are recorded the moment an order is paid, and the rate is the
 * artist's own (up to MAX_ROYALTY_RATE_PCT). Paid at month end, that let an
 * artist buy their own piece at a huge rate with a stolen card and be paid by
 * bank transfer before the chargeback landed (22 Oct 2026 review). Barath's
 * call: rates stay free, but only orders delivered - and still delivered -
 * past the window a refund or dispute usually arrives in are paid.
 */
export const ROYALTY_HOLD_DAYS_AFTER_DELIVERY = 10;
/** Whether a royalty on this order may be paid now. */
export function royaltyCleared(order, now) {
    if (order?.status !== 'DELIVERED')
        return false;
    const deliveredAt = order.timeline?.find((t) => t.status === 'DELIVERED')?.at;
    return typeof deliveredAt === 'number'
        && deliveredAt + ROYALTY_HOLD_DAYS_AFTER_DELIVERY * 24 * 60 * 60 * 1000 <= now;
}
//# sourceMappingURL=marketplace.js.map